<template>
  <AjaxBar
    ref="bar"
    position="top"
    animate="false"
    color="primary"
    size="2px"
    indeterminate
  />
  <q-layout view="lHh LpR lFf">
    <q-header
      bordered
      class="bg-page"
    >
      <q-toolbar style="height: 64px">
        <q-btn
          dense
          flat
          round
          :icon="drawer ? 'menu_open' : 'menu'"
          color="primary"
          @click="toggleDrawer"
        />
        <div class="q-ml-lg  text-secondary">
          <template v-if="!drawer">
            <span class=" text-bold">{{ $page.props.shared.wg.name }} </span>

            <span class="q-px-sm">|</span>
          </template>
          <span>
            {{ $page?.props?.i18n?.summary }}
          </span>
        </div>

        <q-space />

        <UserMenu :locale="$page.props.auth.locale" />
      </q-toolbar>
    </q-header>

    <q-drawer
      v-model="drawer"
      show-if-above
      bordered
      side="left"
      :width="290"
      class="bg-drawer-background"
      content-class=""
    >
      <div class="column justify-between no-wrap fit">
        <q-list class="q-pa-lg">
          <!-- Workspace Group Icon -->
          <q-item class="q-pa-none">
            <q-item-section avatar>
              <avatar-image
                :img-url="$page.props.shared.wg.icon_url"
                :label="$page.props.shared.wg.name"
                avatar-size="42px"
                img-size="42px"
              />
            </q-item-section>

            <q-item-section>
              <q-item-label class="text-bold">
                {{ $page.props.shared.wg.name }}
              </q-item-label>
              <q-item-label caption>
                {{ $page.props.shared.wg.type_label }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>

        <!-- Main Menu -->
        <q-list
          class="q-mt-lg q-pa-none"
          rounded
        >
          <template
            v-for="(item, index) in $page.props.shared.menu_items"
          >
            <!-- Enabled menu item -->
            <Link
              v-if="!item.isSeparator && !item.disabled"
              :key="`item-${index}`"
              :href="item.link"
            >
              <q-item
                v-ripple
                clickable
                dense
                style="min-height: 38px"
                class="q-mx-md q-my-sm main-menu-link q-mt-none"
                :active="item.is_active"
                draggable="false"
              >
                <q-item-section avatar>
                  <q-icon
                    :name="item.icon"
                    size="18px"
                  />
                </q-item-section>
                <q-item-section>
                  <q-item-label class="text-weight-medium">
                    {{ item.title }}
                  </q-item-label>
                </q-item-section>

                <q-item-section side>
                  <q-badge
                    v-if="item.display_badge"
                    :label="item.badge_value"
                    class="list-item-badge"
                  />
                </q-item-section>
              </q-item>
            </Link>

            <!-- Disabled menu item (subscription required) -->
            <q-item
              v-else-if="!item.isSeparator && item.disabled"
              :key="`item-disabled-${index}`"
              dense
              style="min-height: 38px; opacity: 0.4; cursor: not-allowed;"
              class="q-mx-md q-my-sm main-menu-link q-mt-none"
            >
              <q-item-section avatar>
                <q-icon
                  :name="item.icon"
                  size="18px"
                />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-weight-medium">
                  {{ item.title }}
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-icon
                  name="las la-lock"
                  size="14px"
                />
              </q-item-section>
            </q-item>

            <q-separator
              v-else
              :key="`separator-${index}`"
              inset
              class="q-my-md"
            />
          </template>

          <!-- Support -->
          <q-separator
            inset
            class="q-my-md"
          />

          <q-item
            v-ripple
            clickable
            style="min-height: 38px"
            dense
            class="q-mx-md q-my-sm main-menu-link"
            draggable="false"
            @click="$intercom.messages_open($page.props.shared.intercom)"
          >
            <q-item-section avatar>
              <q-icon
                name="las la-headset"
                size="18px"
              />
            </q-item-section>
            <q-item-section>
              <q-item-label class="text-weight-medium">
                {{ $page.props.shared.submit_issue }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>

        <!-- Vertical spacer -->
        <q-item class="col-grow q-py-none" />

        <!-- Workpace Group Logo -->
        <q-list class="q-py-md">
          <q-item class="q-px-md text-center">
            <q-img
              :alt="$page.props.shared.wg.name"
              :src="$page.props.shared.wg.logo_light_url"
              style="height: 22px; margin: auto"
              fit="contain"
            />
          </q-item>
        </q-list>
      </div>
    </q-drawer>

    <q-page-container>
      <slot />
    </q-page-container>
  </q-layout>
</template>

<script>
// Quasar language packs
import frQuasar from "quasar/lang/fr";
import enQuasar from "quasar/lang/en-US";

import AjaxBar from '@/shared/ajax_bar.vue';
import AvatarImage from '@/shared/avatar_image.vue';
import UserMenu from '@/shared/user_menu.vue';
import { Link } from '@inertiajs/vue3';

export default {
  components: {
    AjaxBar,
    AvatarImage,
    Link,
    UserMenu,
  },
  data() {
    return {
      drawer: false,
    };
  },
  beforeUpdate() {
    this.switch_locale(this.$page.props.auth.locale);
  },
  beforeMount() {
    this.switch_locale(this.$page.props.auth.locale);
  },
  mounted() {
    this.$intercom.initialize(this.$page.props.shared.intercom.settings);
    this.$intercom.launcher_show();
  },
  unmounted() {
    this.$intercom.launcher_close();
  },
  methods: {
    toggleDrawer() {
      this.drawer = !this.drawer;
    },
    switch_locale(locale) {
      let new_locale;
      switch (locale) {
      case "fr":
        new_locale = frQuasar;
        break;
      case "en":
        new_locale = enQuasar;
        break;
      default:
        new_locale = frQuasar;
      }
      if (new_locale.isoName != this.$q.lang.isoName) {
        this.$q.lang.set(new_locale);
      }
    }
  },
};
</script>
<style>
.q-page {
  padding-bottom: 70px;
}
</style>
